/**
 * OVERFRAME (2026-10-08, Matt: "portions of this roof on the flanks that I want to set up as
 * 'overframed' or california framed over the main roof structure that runs under it … In Revit I
 * would do this by using a vertical cut … aligned to where the geometry joined. That always felt
 * like a workaround."). A roof OVERFRAMES onto any number of other roofs (`overframeRoofIds`):
 * wherever one of those roofs' TOP surface rides at or above this roof's top, inside that roof's
 * bearing footprint, this roof is not built — the edge left behind is the VALLEY line. The roofs
 * underneath are untouched (they run whole beneath the overframe, which is California framing).
 *
 * The removed regions are derived LIVE from the two solves every rebuild (nothing traced, so they
 * follow a changed pitch, plate or footprint) and handed to the Vertical Cut machinery as extra cut
 * loops (`__overframeCuts`, read by verticalCuts.ts#roofCutRings): the body, eave crops, fascia and
 * gutter runs (ending square at the valley, downspouts at the new run end), tails, every derived
 * member and the plan outline all stop at the valley through the one path. Unlike Join it never
 * adopts the other roof's eave settings and takes many roofs. Exception-proof: any failure = no
 * overframe, never a blank roof.
 */
import type { Pt2, ResolvedElement } from '@aj/bim-kernel'
import { clipperReady, differenceClosed, inflateClosedRing, intersectClosed, signedArea, unionClosed } from '../../clipper'
import { solveRoofForElement, type ResolvedRoofSolve } from './solve'
import { clipHalfPlane } from './join'

/** slivers left by the clipper round trip are not real regions */
const MIN_REGION_SF = 0.05
/** how far a removed region reaches past the roof's outer edge — past the fascia + gutter */
const EDGE_REACH_FT = 1.5

export function overframeTargetIds(el: ResolvedElement): string[] {
  const raw = el.params.overframeRoofIds
  if (!Array.isArray(raw)) return []
  return [...new Set(raw.filter((v): v is string => typeof v === 'string' && !!v && v !== el.id))]
}

/**
 * the regions of this roof the overframe targets take away, as CCW plan rings: wherever a target
 * face's TOP surface rides at or above this roof's top — over the target's WHOLE face, overhang
 * included. (Join stops at the target's wall, the r7 rule, because a wing butts into a wall and
 * passes under a mere overhang band; an overframe sits ON the roof below, so under that roof's
 * overhang there is nothing of it to build — Matt, Santiago: the gutters still ran there.)
 */
export function overframeRegions(el: ResolvedElement, solved: ResolvedRoofSolve, byId: Map<string, ResolvedElement>): Pt2[][] {
  const ids = overframeTargetIds(el)
  if (!ids.length || !clipperReady() || !solved.faces.length) return []
  try {
    const tOwn = el.assemblyThicknessFt
    const under: Pt2[][] = []
    for (const id of ids) {
      const target = byId.get(id)
      if (!target || target.category !== 'roof') continue
      const ts = solveRoofForElement(target)
      if (!ts || !ts.faces.length) continue
      const tTgt = target.assemblyThicknessFt
      for (const f of solved.faces) {
        const dvO = tOwn * Math.sqrt(1 + f.m * f.m)
        for (const g of ts.faces) {
          const dvT = tTgt * Math.sqrt(1 + g.m * g.m)
          const above = clipHalfPlane(g.ring, (p: Pt2) => (g.zAt(p) + dvT) - (f.zAt(p) + dvO))
          if (above.length < 3 || Math.abs(signedArea(above)) <= MIN_REGION_SF) continue
          under.push(...intersectClosed([f.ring], [above]))
        }
      }
    }
    const removed = unionClosed(under).filter((r) => r.length >= 3 && Math.abs(signedArea(r)) > MIN_REGION_SF)
    if (!removed.length) return []
    const before = solved.faces.map((f) => f.ring)
    const kept = differenceClosed(before, removed)
    // reach PAST the roof's outer edge (never into the part that's kept): the removed region's
    // outline otherwise sits exactly ON the eave / rake line, and the fascia + gutter runs
    // (trim.ts — a run is cut where a cut ring COVERS it) read a run lying on a ring's own edge
    // as uncovered, so they kept running the full roof
    const grown = removed.flatMap((r) => inflateClosedRing(r, EDGE_REACH_FT)).filter((r) => r.length >= 3)
    const reach = kept.length ? differenceClosed(grown, kept) : grown
    // every consumer reads each ring as a solid cut, so a HOLE (a kept island fully ringed by
    // removed area — a cricket's saddle) must never come back as a ring of its own: flipped to
    // CCW it used to cut the very island it bounded (CRICKET, 2026-10-19)
    return holeFree(unionClosed([...removed, ...reach]))
      .filter((r) => r.length >= 3 && Math.abs(signedArea(r)) > MIN_REGION_SF)
  } catch {
    return []
  }
}

/**
 * Clipper output (outer rings CCW, holes CW) → solid CCW rings with NO holes: a region holding a
 * hole is sliced along a horizontal line through that hole, which opens the hole into a notch on
 * each half; repeated until no hole is left. The pieces' union is exactly the region.
 */
function holeFree(rings: Pt2[][], depth = 0): Pt2[][] {
  const hole = rings.find((r) => r.length >= 3 && signedArea(r) < 0)
  if (!hole || depth > 12) return rings.filter((r) => r.length >= 3 && signedArea(r) > 0)
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const r of rings) for (const [x, y] of r) {
    if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y
  }
  let hy0 = Infinity, hy1 = -Infinity
  for (const [, y] of hole) { if (y < hy0) hy0 = y; if (y > hy1) hy1 = y }
  const cut = (hy0 + hy1) / 2
  const pad = 1
  const box = (y0: number, y1: number): Pt2[] => [[minX - pad, y0], [maxX + pad, y0], [maxX + pad, y1], [minX - pad, y1]]
  return [
    ...holeFree(intersectClosed(rings, [box(minY - pad, cut)]), depth + 1),
    ...holeFree(intersectClosed(rings, [box(cut, maxY + pad)]), depth + 1),
  ]
}

const memo = new WeakMap<ResolvedElement, ResolvedElement>()

/**
 * The roof with its overframe regions stamped on as cut loops (`params.__overframeCuts`) — the
 * same object back when it overframes nothing. Memoized per resolved roof object, so the body, the
 * downspout reader and the rest of one rebuild share one derivation.
 */
export function withOverframe(el: ResolvedElement, byId: Map<string, ResolvedElement> | undefined): ResolvedElement {
  if (el.category !== 'roof' || !byId || !overframeTargetIds(el).length) return el
  const hit = memo.get(el)
  if (hit) return hit
  let out = el
  const solved = solveRoofForElement(el)
  if (solved) {
    const rings = overframeRegions(el, solved, byId)
    if (rings.length) out = { ...el, params: { ...el.params, __overframeCuts: rings as never } }
  }
  memo.set(el, out)
  return out
}
